<script setup>
import { computed } from "vue";
import ArgonRadio from "@/components/ArgonRadio.vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonCheckbox from "@/components/ArgonCheckbox.vue";
import ArgonButton from "@/components/ArgonButton.vue";
import AutoComplete from "@/components/auto-complete/AutoComplete.vue";
import SectionCard from "@/components/SectionCard.vue";

const props = defineProps({
  carrierType: { type: String, default: "PROPIO" },
  carrierName: { type: String, default: "" },
  carrierNameError: { type: Boolean, default: false },
  carrierSuggestions: { type: Array, default: () => [] },
  carrierVatNumber: { type: String, default: "" },
  carrierVatNumberError: { type: Boolean, default: false },
  carrierAddress: { type: String, default: "" },
  carrierSave: { type: Boolean, default: false },
  vehicleRegistration: { type: String, default: "" },
  vehicleRegistrationError: { type: Boolean, default: false },
  vehicleTrailerRegistration: { type: String, default: "" },
  vehicleSpecialAuthorization: { type: String, default: "" },
});

const emit = defineEmits([
  "update:carrierType",
  "update:carrierName",
  "update:carrierVatNumber",
  "update:carrierAddress",
  "update:carrierSave",
  "update:vehicleRegistration",
  "update:vehicleTrailerRegistration",
  "update:vehicleSpecialAuthorization",
  "carrier-name-input",
  "select-carrier-suggestion",
  "prev",
  "next",
]);

const carrierTypeModel = computed({
  get: () => props.carrierType,
  set: (value) => emit("update:carrierType", value),
});
const carrierNameModel = computed({
  get: () => props.carrierName,
  set: (value) => emit("update:carrierName", value),
});
const carrierVatNumberModel = computed({
  get: () => props.carrierVatNumber,
  set: (value) => emit("update:carrierVatNumber", value),
});
const carrierAddressModel = computed({
  get: () => props.carrierAddress,
  set: (value) => emit("update:carrierAddress", value),
});
const carrierSaveModel = computed({
  get: () => props.carrierSave,
  set: (value) => emit("update:carrierSave", value),
});
const vehicleRegistrationModel = computed({
  get: () => props.vehicleRegistration,
  set: (value) => emit("update:vehicleRegistration", value),
});
const vehicleTrailerRegistrationModel = computed({
  get: () => props.vehicleTrailerRegistration,
  set: (value) => emit("update:vehicleTrailerRegistration", value),
});
const vehicleSpecialAuthorizationModel = computed({
  get: () => props.vehicleSpecialAuthorization,
  set: (value) => emit("update:vehicleSpecialAuthorization", value),
});

const isTercero = computed(() => props.carrierType === "TERCERO");
</script>

<template>
  <div
    class="multisteps-form__panel js-active p-2 position-relative bg-white rounded-3"
    data-animation="FadeIn"
  >
    <div class="d-flex flex-column" style="height: 420px; overflow-y: auto; overflow-x: hidden;">
      <div class="text-center mb-3">
        <p class="mb-0">¿Transporte propio o subcontratado?</p>
      </div>

      <div class="d-flex justify-content-center gap-4 text-center mb-3">
        <div class="d-inline-flex align-items-center gap-2 mb-0">
          <span>Transporte propio</span>
          <argon-radio
            id="deca-carrier-propio"
            v-model="carrierTypeModel"
            name="PROPIO"
            group-name="deca-carrier-type"
          />
        </div>
        <div class="d-inline-flex align-items-center gap-2 mb-0">
          <span>Transporte subcontratado</span>
          <argon-radio
            id="deca-carrier-tercero"
            v-model="carrierTypeModel"
            name="TERCERO"
            group-name="deca-carrier-type"
          />
        </div>
      </div>

      <div class="row">
        <div class="col-6">
          <section-card title="Transportista">
            <div class="row modal-form-row">
              <div class="modal-form-label">
                <label for="deca-carrier-name" class="col-form-label required">
                  Razón social
                </label>
              </div>
              <div class="col">
                <auto-complete
                  id="deca-carrier-name"
                  v-model="carrierNameModel"
                  type="text"
                  :max-length="40"
                  :error="carrierNameError"
                  :data-suggestions="carrierSuggestions"
                  :item-text="['name', 'vat_number']"
                  separator=" - "
                  menu-width="300"
                  @text-changed="emit('carrier-name-input', $event)"
                  @selected-change="emit('select-carrier-suggestion', $event)"
                />
              </div>
            </div>
            <div class="row modal-form-row">
              <div class="modal-form-label">
                <label for="deca-carrier-vat" class="col-form-label required">NIF</label>
              </div>
              <div class="col">
                <argon-input
                  id="deca-carrier-vat"
                  v-model="carrierVatNumberModel"
                  type="text"
                  :max-length="25"
                  :error="carrierVatNumberError"
                />
              </div>
            </div>
            <div class="row modal-form-row">
              <div class="modal-form-label">
                <label for="deca-carrier-address" class="col-form-label">
                  Domicilio
                </label>
              </div>
              <div class="col">
                <argon-input
                  id="deca-carrier-address"
                  v-model="carrierAddressModel"
                  type="text"
                  :max-length="120"
                />
              </div>
            </div>
            <argon-checkbox
              v-if="isTercero"
              id="deca-carrier-save"
              v-model="carrierSaveModel"
            >
              Guardar transportista para reutilizar
            </argon-checkbox>
          </section-card>
        </div>

        <div class="col-6">
          <section-card title="Vehículo">
            <div class="row modal-form-row">
              <div class="modal-form-label">
                <label for="deca-vehicle-registration" class="col-form-label required">
                  Matrícula
                </label>
              </div>
              <div class="col">
                <argon-input
                  id="deca-vehicle-registration"
                  v-model="vehicleRegistrationModel"
                  type="text"
                  :max-length="25"
                  :error="vehicleRegistrationError"
                />
              </div>
            </div>
            <div class="row modal-form-row">
              <div class="modal-form-label">
                <label for="deca-vehicle-trailer" class="col-form-label">
                  Matrícula remolque
                </label>
              </div>
              <div class="col">
                <argon-input
                  id="deca-vehicle-trailer"
                  v-model="vehicleTrailerRegistrationModel"
                  type="text"
                  :max-length="25"
                />
              </div>
            </div>
            <div class="row modal-form-row mb-0">
              <div class="modal-form-label">
                <label for="deca-vehicle-authorization" class="col-form-label">
                  Autorización especial
                </label>
              </div>
              <div class="col">
                <argon-input
                  id="deca-vehicle-authorization"
                  v-model="vehicleSpecialAuthorizationModel"
                  type="text"
                  :max-length="40"
                />
              </div>
            </div>
          </section-card>
        </div>
      </div>

      <div class="d-flex mt-auto">
        <argon-button
          class="mb-0 btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Anterior"
          @click.prevent="emit('prev')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-left" aria-hidden="true"></i>
          </span>
        </argon-button>
        <argon-button
          class="mb-0 ms-auto btn-icon-only"
          color="primary"
          variant="outline"
          size="xs"
          aria-label="Siguiente"
          @click.prevent="emit('next')"
        >
          <span class="btn-inner--icon">
            <i class="fas fa-chevron-right" aria-hidden="true"></i>
          </span>
        </argon-button>
      </div>
    </div>
  </div>
</template>

